// QR scanning for the PWA.
//
// Strategy:
//   1. Live camera scan in a fullscreen overlay. Frames are decoded with the
//      native BarcodeDetector API when available (Chromium), otherwise with
//      jsQR (covers iOS/macOS Safari and Firefox).
//   2. File/photo picker fallback when no camera is available or the user
//      denies access.

import jsQR from "jsqr";

const SCAN_TIMEOUT_MS = 60000;
const MAX_DECODE_DIM = 640; // downscale frames for jsQR performance

function hasBarcodeDetector() {
  return "BarcodeDetector" in window;
}

/* ---------------- Overlay UI ---------------- */

function buildOverlay() {
  const overlay = document.createElement("div");
  overlay.id = "qr-scan-overlay";
  overlay.style.cssText = [
    "position:fixed", "inset:0", "z-index:10050", "background:#000",
    "display:flex", "flex-direction:column", "align-items:center", "justify-content:center",
  ].join(";");

  const video = document.createElement("video");
  video.setAttribute("playsinline", ""); // required for iOS inline playback
  video.muted = true;
  video.style.cssText = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;";

  // Cyan viewfinder over a deep-teal scrim, per the design system.
  const frame = document.createElement("div");
  frame.style.cssText = [
    "position:relative", "width:min(70vw,70vh,300px)", "aspect-ratio:1",
    "border:2px solid #54B6B9", "border-radius:16px",
    "box-shadow:0 0 0 100vmax rgba(8,52,56,.8)",
  ].join(";");

  const hint = document.createElement("p");
  hint.textContent = "POINT THE CAMERA AT A QR CODE";
  hint.style.cssText = "position:absolute;bottom:104px;color:#F8F8F8;font:400 12px 'Fira Mono',ui-monospace,monospace;letter-spacing:.12em;";

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.textContent = "Cancel";
  cancel.style.cssText = [
    "position:absolute", "bottom:40px", "padding:12px 28px",
    "border:1px solid #F8F8F8", "border-radius:0",
    "background:transparent", "color:#F8F8F8",
    "font:500 14px Inter,-apple-system,system-ui,sans-serif",
    "cursor:pointer",
  ].join(";");

  overlay.append(video, frame, hint, cancel);
  return { overlay, video, cancel };
}

/* ---------------- Frame decoding ---------------- */

async function decodeCanvasWithDetector(detector, source) {
  const barcodes = await detector.detect(source);
  return barcodes.length ? barcodes[0].rawValue : null;
}

function decodeImageDataWithJsQR(imageData) {
  const result = jsQR(imageData.data, imageData.width, imageData.height, {
    inversionAttempts: "dontInvert",
  });
  return result?.data || null;
}

/* ---------------- Scanner ---------------- */

const Scanner = {
  async scan() {
    if (navigator.mediaDevices?.getUserMedia) {
      try {
        return await this._scanWithCamera();
      } catch (err) {
        if (err?.name === "ScanCancelled") throw new Error("Scan cancelled");
        console.warn("Camera scan unavailable, falling back to file picker:", err?.message || err);
      }
    }
    return this._scanWithFileInput();
  },

  async _scanWithCamera() {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "environment" },
      audio: false,
    });

    const { overlay, video, cancel } = buildOverlay();
    document.body.appendChild(overlay);
    video.srcObject = stream;

    const detector = hasBarcodeDetector()
      ? new BarcodeDetector({ formats: ["qr_code"] })
      : null;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    let finished = false;
    const cleanup = () => {
      finished = true;
      stream.getTracks().forEach((t) => t.stop());
      overlay.remove();
    };

    try {
      return await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Scan timeout"));
        }, SCAN_TIMEOUT_MS);

        cancel.addEventListener("click", () => {
          clearTimeout(timeout);
          const e = new Error("Scan cancelled");
          e.name = "ScanCancelled";
          reject(e);
        });

        const scanFrame = async () => {
          if (finished) return;
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            try {
              let value = null;
              if (detector) {
                value = await decodeCanvasWithDetector(detector, video);
              } else {
                const scale = Math.min(1, MAX_DECODE_DIM / Math.max(video.videoWidth, video.videoHeight));
                canvas.width = Math.floor(video.videoWidth * scale);
                canvas.height = Math.floor(video.videoHeight * scale);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                value = decodeImageDataWithJsQR(ctx.getImageData(0, 0, canvas.width, canvas.height));
              }
              if (value) {
                clearTimeout(timeout);
                resolve(value);
                return;
              }
            } catch (e) {
              console.warn("Detection error:", e);
            }
          }
          requestAnimationFrame(scanFrame);
        };

        video.play().then(scanFrame).catch(reject);
      });
    } finally {
      cleanup();
    }
  },

  _scanWithFileInput() {
    return new Promise((resolve, reject) => {
      const input = document.getElementById("fileInput");
      if (!input) {
        return reject(new Error('Missing <input id="fileInput"> in HTML'));
      }

      const onChange = async (e) => {
        const file = e.target.files?.[0];
        input.removeEventListener("change", onChange);
        input.value = "";
        if (!file) return reject(new Error("No file selected"));

        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = async () => {
          try {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            let value = null;
            if (hasBarcodeDetector()) {
              const detector = new BarcodeDetector({ formats: ["qr_code"] });
              value = await decodeCanvasWithDetector(detector, canvas);
            }
            if (!value) {
              value = decodeImageDataWithJsQR(ctx.getImageData(0, 0, canvas.width, canvas.height));
            }
            if (value) resolve(value);
            else reject(new Error("No QR found"));
          } catch (err) {
            reject(err);
          } finally {
            URL.revokeObjectURL(img.src);
          }
        };
        img.onerror = () => reject(new Error("Image load failed"));
      };

      input.addEventListener("change", onChange);
      input.click();
    });
  },
};

export { Scanner };
